import { useEffect, useRef, useState } from "react";
import type { Card, CardAction, CardResult, ChatItem, ChatTurn, ConversationSummary, DraftState, SelectedMessageRef } from "./types";
import { deleteConversation, HistoryUnavailable, listConversations, loadConversation, NotAuthenticatedYet, sendChat, createHrReplyDraft, reportComposeProbe, getSignatureState, SignatureUnavailable, type SignatureState } from "./api/client";
import { SignatureSetup } from "./components/SignatureSetup";
import { OutsideOutlook } from "./components/OutsideOutlook";
import { Feedback } from "./components/Feedback";
import { initOffice, onHostReady, isInOutlook, getCurrentEmailId, getCurrentEmailMeta, getCurrentEmail, getRestItemId, getInternetMessageId, getLastSsoError, getSelectedMessages, onItemChanged, onSelectedItemsChanged, displayReplyForm, displayNewMessageForm, displayExistingMessage, openUrl } from "./office/office";
import type { ComposeOutcome } from "./office/office";
import { Bubble } from "./components/Bubble";
import { Typing } from "./components/Typing";
import { CardView } from "./components/CardView";
import { Chips } from "./components/Chips";
import { Composer } from "./components/Composer";
import { ConversationMenu } from "./components/ConversationMenu";
// ChatItem now lives in types.ts alongside the rest of the wire contract, because
// the server persists exactly this shape (docs/CONVERSATION-STORE.md).

// DELIBERATELY SAYS NOTHING ABOUT THE SHARED MAILBOX.
//
// It used to promise "look through the HR shared mailbox" — unconditionally, in a
// hardcoded constant. But mailbox access is a per-environment FLAG
// (GRAPH_MAILBOX_ENABLED), and v1 ships with it OFF in prod. The orchestrator's
// system prompt correctly tells those users it can only see the open message, so
// the very first thing a BayMark HR user read was a promise the next turn refused.
//
// Fixed by removing the claim rather than by branching on the flag: a neutral
// greeting states only what is true in EVERY environment, cannot flash the wrong
// text while /healthz is in flight, and cannot drift out of sync with the flag
// later. The model still offers mailbox help when the flag is on, because it is
// told about it — that is the one place the capability is described, and it is
// already conditional.
const GREETING =
  "Hi! I'm the <b>BayMark HR Assistant</b>. I can answer HR policy questions from your documents, summarize the email you're reading, and draft replies you can edit. What would you like to do?";
// STARTING chips only — every chip after the first turn is generated by the model
// from what was actually just discussed (see sanitizeSuggestions in the
// orchestrator). These are the one place a generic prompt is right, because there is
// no conversation yet to be specific about.
//
// "What's in the HR mailbox?" was removed from both lists 2026-08-05: it opens with
// the pane's least useful capability, and in dev it reads a mailbox
// (hr-assistant@phiflow.co) that isn't the signed-in user's, which reads as a bug.
// Mailbox questions still work when typed.
//
// With an email open. Deliberately leads with the email-specific actions.
const DEFAULT_CHIPS = ["Summarize this email", "Draft a response for this email", "Ask a policy question"];
// No message selected — the email-specific chips above would have nothing to act
// on, so lead with everything that works without one. The pane is fully usable
// here; it is NOT a degraded state.
const NO_EMAIL_CHIPS = ["How does PTO carryover work?", "Draft a new email", "Ask a policy question"];
// Launched on 2+ selected messages — the selection is the subject of the request.
const MULTI_SELECT_CHIPS = ["Summarize these messages", "Which of these need a reply?", "Ask a policy question"];
const MIN_THINK_MS = 500;
// How long to wait for the Outlook host before treating its absence as real and
// showing the "open this in Outlook" gate. Matches office.ts's HOST_READY_TIMEOUT_MS
// deliberately: a shorter value here would gate a user whom office.ts is still
// willing to connect, and the two must not disagree about what "no host" means.
const HOST_GATE_MS = 8000;
const STORAGE_KEY = "baymark-hr-chat-v1";

// Short form for toasts (the HR-mailbox draft path), long form for the chat bubble.
const SSO_TOAST = "Couldn't sign you in to the HR Assistant — your account may not have access yet.";

/** What to actually tell a user whose request came back 401/403.
 *
 *  This replaced a single "Something went wrong reaching the assistant" bubble.
 *  That message is what a user sees when their Entra account simply isn't assigned
 *  to the add-in's app registration — a permissions problem with a one-line admin
 *  fix, reported as an outage. The Office SSO code is included verbatim because it
 *  is the only thing that separates these causes, and docs/SSO.md indexes them.
 *
 *  Only codes whose meaning is unambiguous get bespoke wording; everything else
 *  gets the honest general case rather than a confident guess. */
function ssoFailureHtml(code: number | null): string {
  // Plain text, no wrapper class: there is no dim/muted utility in index.css, and
  // inventing one is how the previous-chats menu ended up styled with tokens that
  // don't exist. The code is short enough to read inline.
  const suffix = code ? ` (sign-in code ${code})` : "";
  switch (code) {
    case 13001:
      return `You're not signed in to Office in this window. Sign in to Outlook and reopen the pane.${suffix}`;
    case 13002:
      return `The sign-in consent prompt was dismissed. Reopen the pane and accept it to continue.${suffix}`;
    case 13003:
      return `This add-in only works with a work or school account, not a personal Microsoft account.${suffix}`;
    case 13000:
      return `This build of Outlook can't sign in to the add-in. Try Outlook on the web, or the new Outlook for Windows.${suffix}`;
    default:
      return (
        "I couldn't sign you in, so I can't reach the assistant. This usually means <b>your account hasn't been granted access to this add-in yet</b> " +
        `— an administrator needs to assign it. Nothing is wrong with your mailbox.${suffix}`
      );
  }
}

interface Persisted {
  items: ChatItem[];
  chips: string[];
  draft: DraftState | null;
  history: ChatTurn[];
  /** Which server-side conversation these items belong to, so a reopened pane keeps
   *  appending to it instead of silently starting a second one. */
  conversationId?: string | null;
  /** Whether this deployment had a conversation store last time we checked.
   *  Cached purely to avoid a visible control swap on launch — see historyAvailable. */
  historyAvailable?: boolean;
  /** WHICH EMAIL these messages are about, as `id|subject`.
   *
   *  Persisted so a pane reopened on a DIFFERENT message can say so. A pinned pane
   *  gets ItemChanged and announces the switch; an unpinned one is torn down and
   *  remounted, so the switch happens while nothing is watching — and because
   *  conversations are never auto-cleared, the restored transcript then sits under a
   *  new email with nothing to say the subject changed. */
  lastEmailKey?: string | null;
}

// Restore prior conversation so closing/reopening the pane doesn't start over.
//
// This is now a CACHE, not the source of truth. It makes the pane render instantly
// and survives a transient /api failure, but the server holds the real transcript —
// localStorage here is partitioned third-party iframe storage that the browser
// evicts on its own schedule, which is exactly why history kept vanishing.
function loadPersisted(): Persisted | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Persisted) : null;
  } catch {
    return null;
  }
}

const persisted = loadPersisted();
let uid = persisted?.items?.reduce((m, i) => Math.max(m, i.id), 0) ?? 0;
const nextId = () => ++uid;

export function App() {
  const [items, setItems] = useState<ChatItem[]>(() =>
    persisted?.items?.length ? persisted.items : [{ id: nextId(), kind: "bubble", role: "agent", html: GREETING }],
  );
  const [chips, setChips] = useState<string[]>(() => persisted?.chips ?? DEFAULT_CHIPS);
  const [draft, setDraft] = useState<DraftState | null>(() => persisted?.draft ?? null);
  const [currentEmailId, setCurrentEmailId] = useState<string | null>(() => getCurrentEmailId());
  const [meta, setMeta] = useState(() => getCurrentEmailMeta());
  const [selected, setSelected] = useState<SelectedMessageRef[]>([]);
  const [thinking, setThinking] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [mode, setMode] = useState<"live" | "sample" | null>(null);

  // "Opened as a website instead of from Outlook" gate. Three pieces of state
  // because two of them arrive asynchronously and guessing either way is wrong:
  //   officeOnly — from /healthz; true only on App Service, so local dev is unaffected
  //   hostUp     — is the Outlook host live (may appear late on a cold pane launch)
  //   hostSettled — have we waited long enough to call its absence real?
  // Without `hostSettled` a slow cold launch inside real Outlook would flash
  // "open this in Outlook" at a legitimate user, which is worse than the problem
  // this screen solves. onHostReady() flips it back the moment the host appears,
  // so a late host is corrected rather than stranded.
  const [officeOnly, setOfficeOnly] = useState<boolean | null>(null);
  const [hostUp, setHostUp] = useState(() => isInOutlook());
  const [hostSettled, setHostSettled] = useState(() => isInOutlook());

  // Conversation history.
  //
  // TRI-STATE, and the null matters. It used to start `false`, which rendered the
  // plain "New chat" button and then replaced it with "Previous chats" a moment
  // later once the probe came back — a visible control swap on every launch.
  // `null` means "we don't know yet" and renders NEITHER, so the strip never shows
  // one control morphing into another. The last known answer is cached, so from the
  // second launch onward the right control is there on the first paint.
  const [conversationId, setConversationId] = useState<string | null>(() => persisted?.conversationId ?? null);
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [historyAvailable, setHistoryAvailable] = useState<boolean | null>(() => persisted?.historyAvailable ?? null);
  const [historyLoading, setHistoryLoading] = useState(false);

  // The user's signature for HR-mailbox drafts. null until the store answers; the
  // first-run sheet opens once, for a user who has never saved or skipped one.
  const [signature, setSignature] = useState<SignatureState | null>(null);
  const [signatureSheet, setSignatureSheet] = useState<"first-run" | "settings" | null>(null);
  const firstRunShownRef = useRef(false);

  const chatRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const draftRef = useRef<DraftState | null>(null);
  draftRef.current = draft;
  const historyRef = useRef<ChatTurn[]>(persisted?.history ?? []);
  // Last seen "<id>|<subject>" — so redundant sync signals don't re-announce.
  const lastItemKeyRef = useRef<string | null>(null);
  const selectedRef = useRef<SelectedMessageRef[]>([]);
  selectedRef.current = selected;

  // Two different jobs, and using the wrong one is how markup gets executed.
  //
  // stripHtml is for text handed to the MODEL as conversation history — tags are
  // noise there and removing them is right.
  //
  // escapeHtml is for text interpolated into a bubble's `html`, which Bubble.tsx
  // renders with dangerouslySetInnerHTML. Stripping is the wrong tool for that:
  // it is a blocklist, and a blocklist on untrusted input is a standing bet that
  // nobody finds a construct it misses. An email subject is as untrusted as input
  // gets — anyone on the internet can put one in the HR mailbox — so escape it and
  // the question stops being interesting.
  // stripHtml runs to a fixed point. Its callers today feed the model, where a
  // leftover tag is only noise — but a single removal pass can be reassembled by the
  // removal itself (`<<b>b>` loses the inner tag and the halves close up), so a
  // future caller pointing this at innerHTML would inherit a hole. Cheaper to make
  // the helper honest than to rely on nobody reusing it.
  const stripHtml = (s: string) => {
    let out = s;
    for (let i = 0; i < 8; i++) {
      const next = out.replace(/<[^>]+>/g, "");
      if (next === out) break;
      out = next;
    }
    return out;
  };
  const escapeHtml = (s: string) =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

  useEffect(() => {
    // Re-read the host on demand: the initial read can happen before Office is
    // ready on a cold launch, so this runs again if readiness lands late.
    const syncFromHost = () => {
      const m = getCurrentEmailMeta();
      setMeta(m);
      setCurrentEmailId(getCurrentEmailId());
      return m;
    };

    // Multi-select: Office reports item=null and the selection separately, so this
    // has to be read on its own. Only announce when the COUNT changes, so the
    // several signals that trigger a re-read stay idempotent.
    const syncSelection = async () => {
      const msgs = await getSelectedMessages();
      const refs: SelectedMessageRef[] = msgs.map((m) => ({
        itemId: m.itemId,
        internetMessageId: m.internetMessageId,
        subject: m.subject,
      }));
      const prev = selectedRef.current.length;
      setSelected(refs);
      if (refs.length >= 2 && refs.length !== prev) {
        setChips(MULTI_SELECT_CHIPS);
        pushItem({
          id: nextId(),
          kind: "bubble",
          role: "system",
          html: `${refs.length} messages selected.`,
        });
      }
    };
    void syncSelection();
    const unsubscribeSelection = onSelectedItemsChanged(() => void syncSelection());
    onHostReady(syncFromHost);
    // Re-probe for chat history once Office is up: SSO tokens are only mintable
    // after the host is ready, so the mount-time probe usually 401s in Outlook.
    onHostReady(() => void refreshConversations(true));
    onHostReady(() => void refreshSignature());

    // Announce + re-sync only when the selection ACTUALLY changed. Called from
    // several signals (ItemChanged, window focus, tab visibility) precisely so a
    // single missed signal can't leave a pinned pane showing a stale email —
    // which is what "I clicked into a new email and it stopped working" looked
    // like. Idempotent, so redundant signals are harmless.
    const syncIfChanged = () => {
      // Not seeded until initOffice resolves; a focus event before then would read
      // an empty item and wipe the chat restored for the email the pane opened on.
      if (lastItemKeyRef.current === null) return;
      const m = getCurrentEmailMeta();
      const key = `${m.id ?? ""}|${m.subject}`;
      if (key === lastItemKeyRef.current) return;
      lastItemKeyRef.current = key;
      setMeta(m);
      setCurrentEmailId(m.id);
      // With 2+ messages selected Outlook reports item=null, which is NOT the same
      // as "nothing selected" — the selection sync owns that announcement, so stay
      // quiet here rather than contradicting it.
      if (selectedRef.current.length >= 2) {
        setDraft(null);
        return;
      }
      startFreshFor(m.subject);
    };

    // Belt-and-braces: Outlook doesn't always deliver ItemChanged to a pane it
    // reused, so re-check whenever the pane is shown or regains focus.
    const recheck = () => {
      if (document.visibilityState !== "visible") return;
      syncIfChanged();
      void syncSelection();
    };
    document.addEventListener("visibilitychange", recheck);
    window.addEventListener("focus", recheck);

    // Stay aware when the user selects another message (pinned pane). Registering
    // outside initOffice().then() so a slow host still gets the subscription —
    // onItemChanged defers binding until the host is ready.
    const unsubscribeItemChanged = onItemChanged(syncIfChanged);

    initOffice().then((state) => {
      // initOffice has now made the host determination — via Office.onReady, which
      // fires in a plain browser too (with no mailbox context). That is FAR faster
      // than waiting out HOST_GATE_MS, and it is what stops a browser visitor
      // seeing the pane at all. The timeout below stays as a backstop for a host
      // that never signals either way.
      setHostUp(state.inHost);
      setHostSettled(true);
      const initial = syncFromHost();
      const initialKey = `${initial.id ?? ""}|${initial.subject}`;
      // A CONTEXT SWITCH THE PINNED PANE WOULD HAVE ANNOUNCED, but an unpinned one
      // cannot: it is torn down and remounted, so ItemChanged never fires and the
      // seeding below deliberately suppresses the first announcement.
      //
      // Only when there is a restored conversation to be confused BY, and only when
      // the email actually changed — reopening the pane on the same message must stay
      // silent, or every launch would prepend a line saying nothing happened.
      if (persisted?.items?.length && persisted.lastEmailKey && persisted.lastEmailKey !== initialKey) {
        startFreshFor(initial.subject);
      }
      lastItemKeyRef.current = initialKey;
      // Launched from the top-level Outlook toolbar with no message selected
      // (SupportsNoItemContext). Email-specific chips would have nothing to act
      // on, so lead with what does work here: policy questions and new drafts.
      if (!initial.subject && selectedRef.current.length < 2 && !persisted?.items?.length) {
        setChips(NO_EMAIL_CHIPS);
        pushItem({
          id: nextId(),
          kind: "bubble",
          role: "system",
          // Same rule as GREETING: no unconditional shared-mailbox claim.
          html: "No message is open, so ask me anything about HR policy, or have me draft an email. Open an email and I'll pick it up automatically.",
        });
      }
    });
    fetch("/healthz")
      .then((r) => r.json())
      .then((h) => {
        setMode(h.mock ? "sample" : "live");
        setOfficeOnly(!!h.officeOnly);
      })
      .catch(() => {
        setMode(null);
        // FAIL CLOSED. `officeOnly` staying null means `decided` can never become
        // true outside a host, and the pane would sit blank forever. Assuming
        // "deployed, require Outlook" is the safe end of that choice: inside
        // Outlook hostUp has already decided this and nothing changes, and in a
        // browser the visitor gets the notice rather than a dead grey rectangle.
        // Locally it means a stopped backend shows the notice — which is honest,
        // since the pane cannot work without one either way.
        setOfficeOnly(true);
      });

    // Watch for a late host, and stop waiting after the same backstop office.ts
    // uses. Both paths clear the gate's "still deciding" state.
    onHostReady(() => {
      setHostUp(true);
      setHostSettled(true);
    });
    const hostGate = setTimeout(() => setHostSettled(true), HOST_GATE_MS);

    return () => {
      clearTimeout(hostGate);
      document.removeEventListener("visibilitychange", recheck);
      window.removeEventListener("focus", recheck);
      unsubscribeItemChanged();
      unsubscribeSelection();
    };
  }, []);

  // Cache the conversation locally on every change. The server is authoritative;
  // this just makes reopening the pane instant.
  useEffect(() => {
    try {
      const payload: Persisted = {
        items: items.slice(-60),
        chips,
        draft,
        history: historyRef.current.slice(-16),
        conversationId,
        // NEVER WRITE NULL OVER A GOOD KEY. This effect runs on mount, before
        // initOffice() has resolved and set the ref — so a plain
        // `lastEmailKey: lastItemKeyRef.current` wrote null on every single launch and
        // destroyed the value the announcement below depends on. Open the pane, touch
        // nothing, close it, and the record of which email the conversation was about
        // is gone.
        lastEmailKey: lastItemKeyRef.current ?? persisted?.lastEmailKey ?? null,
        // Only persist a resolved answer; writing null back would undo the caching.
        ...(historyAvailable === null ? {} : { historyAvailable }),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
      /* storage full/unavailable — non-fatal */
    }
    // `meta` IS A DEPENDENCY, and it is the second half of the same bug: the email can
    // change without any of the other values changing (open the pane on a new message
    // and read it without asking anything). Without meta here the effect never re-ran,
    // so the stored key stayed stale and the next launch compared against the wrong
    // email. lastItemKeyRef is already updated by the time meta changes, since both
    // are set by the same sync.
  }, [items, chips, draft, conversationId, historyAvailable, meta]);

  // Probe once for whether this backend has a conversation store. Doubles as the
  // initial dropdown fetch, so opening the menu is instant the first time.
  //
  // Gated on `hostSettled` rather than firing at mount. Inside Outlook the mount
  // probe could NEVER succeed: Office.js has not finished initialising that early,
  // so there is no SSO token, so the request goes out unauthenticated and earns a
  // guaranteed 401 — observed on every single pane launch in a deployed OWA console
  // 2026-08-10. It recovered via the NotAuthenticatedYet retry, so it was only ever
  // noise, but it is the kind of noise that makes a real auth failure invisible.
  //
  // `hostSettled` is true once we know the answer either way, which keeps BOTH
  // paths working: in Outlook it flips when the host arrives (token available), and
  // in a plain browser it flips on the backstop (local dev, REQUIRE_AUTH=false,
  // where an unauthenticated probe is correct and expected).
  useEffect(() => {
    if (!hostSettled) return;
    void refreshConversations(true);
    void refreshSignature();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostSettled]);

  // Autoscroll to newest.
  useEffect(() => {
    const el = chatRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [items, thinking]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  function pushItem(item: ChatItem) {
    setItems((prev) => [...prev, item]);
  }

  /** Swap a bubble in place, keeping its position. Used to reconcile the streamed
   *  lead-in with the server's authoritative text so the transport can't change what
   *  the user ends up reading. */
  function replaceItem(id: number, item: ChatItem) {
    setItems((prev) => prev.map((it) => (it.id === id ? item : it)));
  }

  function removeItem(id: number) {
    setItems((prev) => prev.filter((it) => it.id !== id));
  }

  /** A different email starts a new chat. req.history feeds retrieval and the
   *  model prompt (orchestrator.ts), so carrying it over would answer questions
   *  about this email with the last one's turns in context. */
  function startFreshFor(subject: string) {
    historyRef.current = [];
    setDraft(null);
    setConversationId(null);
    setChips(subject ? DEFAULT_CHIPS : NO_EMAIL_CHIPS);
    setItems([
      {
        id: nextId(),
        kind: "bubble",
        role: "system",
        html: subject ? `Now viewing: ${escapeHtml(subject)}` : "No message selected.",
      },
    ]);
  }

  function resetChat() {
    historyRef.current = [];
    setDraft(null);
    setChips(DEFAULT_CHIPS);
    setItems([{ id: nextId(), kind: "bubble", role: "agent", html: GREETING }]);
    // Drop the server-side thread too, or the next turn would append the new
    // conversation onto the one the user just cleared.
    setConversationId(null);
  }

  /** Fetch the dropdown contents. `probe` marks a background call (mount, or the
   *  host becoming ready), which is also how we learn whether this backend has a
   *  store at all — so it must stay silent about failures.
   *
   *  Retries on 401. Office.js SSO is not available the moment the pane mounts, so
   *  the first probe often goes out with no token; treating that as failure left
   *  "Previous chats" hidden until a chat turn happened to prove the store existed.
   *  Bounded and short, because a genuine auth problem should not retry forever. */
  async function refreshConversations(probe = false, attempt = 0) {
    setHistoryLoading(true);
    try {
      const list = await listConversations();
      setConversations(list);
      setHistoryAvailable(true);
    } catch (err) {
      // 503 means "this deployment has no history store" — hide the UI rather than
      // showing an empty list, which would claim the user has no past chats.
      if (err instanceof HistoryUnavailable) {
        setHistoryAvailable(false);
      } else if (err instanceof NotAuthenticatedYet && attempt < 4) {
        // 0.5s, 1s, 2s, 4s — comfortably covers Office SSO warm-up without
        // hammering, and onHostReady below usually beats it to the punch anyway.
        setTimeout(() => void refreshConversations(probe, attempt + 1), 500 * 2 ** attempt);
      } else if (!probe) {
        setToast("Couldn't load previous chats.");
      }
    } finally {
      setHistoryLoading(false);
    }
  }

  /** Load the signature state; open the first-run sheet when the user has never
   *  saved or skipped one. Retries on 401 for the same SSO warm-up reason as
   *  refreshConversations. A store-less deployment hides the feature. */
  async function refreshSignature(attempt = 0) {
    try {
      const state = await getSignatureState();
      setSignature(state);
      if (state.status === "none" && !firstRunShownRef.current) {
        firstRunShownRef.current = true;
        setSignatureSheet("first-run");
      }
    } catch (err) {
      if (err instanceof SignatureUnavailable) setSignature(null);
      else if (err instanceof NotAuthenticatedYet && attempt < 4) {
        setTimeout(() => void refreshSignature(attempt + 1), 500 * 2 ** attempt);
      }
    }
  }

  function closeSignatureSheet(result: "saved" | "skipped" | "removed" | "closed", html?: string | null) {
    setSignatureSheet(null);
    if (result === "saved") {
      setSignature((s) => (s ? { ...s, status: "confirmed", html: html ?? null } : s));
      setToast("Signature saved. It will be added to your HR drafts.");
    } else if (result === "removed") {
      setSignature((s) => (s ? { ...s, status: "skipped", html: null } : s));
      setToast("Signature removed from HR drafts.");
    } else if (result === "skipped") {
      setSignature((s) => (s ? { ...s, status: "skipped" } : s));
      setToast("You can add it any time from Signature at the top.");
    }
  }

  /** Replay a stored conversation. Messages were persisted as the same ChatItem
   *  shape the pane renders, so this is a re-id and nothing more. */
  async function openConversation(id: string) {
    setHistoryLoading(true);
    try {
      const { summary, messages } = await loadConversation(id);
      const restored: ChatItem[] = messages
        .map((m) => m.body)
        .filter((b): b is ChatItem => !!b)
        .map((b) => ({ ...b, id: nextId() }));

      if (summary.contentPurged) {
        restored.unshift({
          id: nextId(),
          kind: "bubble",
          role: "system",
          // Names no window. Retention is a SETTING (config.conversationRetentionMonths,
          // and as of 2026-08-26 it is off), so a number hardcoded here is a claim the
          // pane cannot actually verify and the exact way a setting starts lying to
          // users. This only renders for conversations purged while a window WAS in
          // force, which is now a historical state rather than an ongoing one.
          html: "Some of this chat's older messages are no longer available.",
        });
      }
      if (!restored.length) {
        restored.push({ id: nextId(), kind: "bubble", role: "system", html: "This chat has no messages left to show." });
      }

      setItems(restored);
      setConversationId(id);
      setDraft(null);
      // Rebuild model context from the restored bubbles so a resumed conversation
      // is coherent to the model, not just to the reader.
      historyRef.current = messages
        .filter((m) => m.body?.kind === "bubble")
        .map((m) => ({
          role: m.role === "user" ? ("user" as const) : ("assistant" as const),
          text: stripHtml((m.body as { html: string }).html),
        }))
        .slice(-16);
    } catch {
      setToast("Couldn't open that chat.");
    } finally {
      setHistoryLoading(false);
    }
  }

  async function removeConversation(id: string) {
    // Optimistic: the row disappears immediately, which is what makes this feel
    // like tidying rather than a round-trip.
    setConversations((prev) => prev.filter((c) => c.id !== id));
    try {
      await deleteConversation(id);
      if (id === conversationId) resetChat();
    } catch {
      setToast("Couldn't remove that chat.");
      void refreshConversations();
    }
  }

  async function runTurn(message: string, emailId?: string | null, echo = true) {
    if (echo) pushItem({ id: nextId(), kind: "bubble", role: "user", html: message });
    historyRef.current.push({ role: "user", text: message });
    setThinking(true);
    const startedAt = Date.now();
    try {
      const email = await getCurrentEmail(); // real content in Outlook; null in browser
      // The assistant's lead-in arrives seconds before the card. Showing it as soon as it
      // lands is the difference between an 11-second blank spinner and an immediate reply;
      // the card still appears when the full response does. `streamedId` remembers the
      // bubble so the authoritative text from the `done` event replaces it rather than
      // being appended underneath — the rendered result must not depend on the transport.
      let streamedId: number | null = null;
      const res = await sendChat(
        {
          message,
          currentEmailId: emailId !== undefined ? emailId : currentEmailId,
          email,
          history: historyRef.current.slice(-8),
          draft: draftRef.current,
          selectedMessages: selectedRef.current.length >= 2 ? selectedRef.current : null,
          conversationId,
        },
        (text) => {
          if (!text.trim() || streamedId !== null) return;
          streamedId = nextId();
          setThinking(false); // the reply is on screen; the typing indicator is now a lie
          pushItem({ id: streamedId, kind: "bubble", role: "agent", html: text });
        },
      );
      // The floor only applies when nothing has been shown yet. Holding a reply the user
      // can already see would be the opposite of what it is for.
      const elapsed = Date.now() - startedAt;
      if (streamedId === null && elapsed < MIN_THINK_MS) await new Promise((r) => setTimeout(r, MIN_THINK_MS - elapsed));

      if (streamedId !== null) {
        // Reconcile: the server's first bubble is authoritative. If the turn ended up with
        // no bubbles at all (a refusal path rewrites them), drop the streamed one.
        const id = streamedId as number;
        if (res.bubbles[0]) replaceItem(id, { id, kind: "bubble", role: "agent", html: res.bubbles[0] });
        else removeItem(id);
        for (const b of res.bubbles.slice(1)) pushItem({ id: nextId(), kind: "bubble", role: "agent", html: b });
      } else {
        for (const b of res.bubbles) pushItem({ id: nextId(), kind: "bubble", role: "agent", html: b });
      }
      if (res.bubbles[0]) historyRef.current.push({ role: "assistant", text: stripHtml(res.bubbles[0]) });
      if (res.card) pushItem({ id: nextId(), kind: "card", card: res.card });
      if (!res.bubbles.length && !res.card) {
        pushItem({ id: nextId(), kind: "bubble", role: "agent", html: "I didn't catch that. Could you rephrase, or tap a suggestion below?" });
      }
      // Offer a rating on turns where "was this right?" is a meaningful question.
      // NOT on every turn: a thumbs control under "Hi! How can I help?" is noise,
      // and noise is what teaches people to ignore the control entirely — which
      // would cost us the one direct quality signal we have. usageEventId is null
      // when there is no database, in which case the control would do nothing.
      if (res.usageEventId && (res.card || res.bubbles.length)) {
        pushItem({ id: nextId(), kind: "feedback", usageEventId: res.usageEventId, rating: null });
      }
      if (res.chips) setChips(res.chips);
      if (res.draft !== undefined) setDraft(res.draft);
      if (res.focusInput) inputRef.current?.focus();
      // First turn of a new chat: adopt the id the server assigned, so subsequent
      // turns append to this conversation instead of creating one each time.
      if (res.conversationId && res.conversationId !== conversationId) {
        setConversationId(res.conversationId);
        setHistoryAvailable(true);
      }
    } catch (err) {
      // An auth failure is not an outage and must not be reported as one. This
      // single bubble used to cover both, and a user whose account wasn't assigned
      // to the add-in's Entra app saw "something went wrong" forever with no hint
      // that an admin could fix it in one command.
      const html =
        err instanceof NotAuthenticatedYet
          ? ssoFailureHtml(getLastSsoError())
          : "Something went wrong reaching the assistant. Please try again.";
      pushItem({ id: nextId(), kind: "bubble", role: "system", html });
    } finally {
      setThinking(false);
    }
  }

  /** The draft a card's button acts on.
   *
   *  THE CARD, not `draftRef`. This is the 2026-08-26 "the drafted reply was empty"
   *  bug, and it was not a compose problem at all: `draftRef` holds ONE draft, the
   *  most recent, and it is set to null in two places that leave the card sitting on
   *  screen with its buttons live —
   *
   *    - restoring a saved conversation (setDraft(null) right after setItems), which
   *      replays every draft card but restores no draft state, and
   *    - switching to a different message ("a fresh draft context").
   *
   *  Either one turns the next click into displayReplyForm(""), which Outlook accepts,
   *  so the user got a popout compose window containing nothing and a toast saying the
   *  draft had been placed in it.
   *
   *  `card.draftPreview` is the text rendered in that card, so it cannot drift from
   *  what the person was looking at when they clicked, it survives both nullings, and
   *  clicking the button on an older draft now gives THAT draft rather than silently
   *  substituting a newer one. draftRef stays as a fallback only for cards that
   *  predate a preview. */
  function draftTextFor(card?: Card): string {
    return (card?.draftPreview ?? draftRef.current?.text ?? "").trim();
  }

  /** One place to say what happened, so no caller can claim success for an empty
   *  window again. */
  function composeToast(outcome: ComposeOutcome, ok: string, noHost: string) {
    switch (outcome) {
      case "ok":
        return setToast(ok);
      case "empty":
        return setToast("That draft is no longer available — ask me to draft the reply again and I'll rebuild it.");
      case "too_large":
        return setToast("That draft is too long for Outlook to prefill. Ask me to shorten it and try again.");
      case "no_host":
        return setToast(noHost);
      default:
        return setToast("Outlook wouldn't open the compose window. Try again, or copy the draft across manually.");
    }
  }

  function onAction(action: CardAction, card?: Card) {
    const emailId = (action.data?.emailId as string) ?? currentEmailId ?? undefined;
    switch (action.id) {
      case "openCompose": {
        composeToast(
          displayReplyForm(draftTextFor(card)),
          "Draft placed in the Outlook reply editor.",
          "In Outlook, this opens the reply editor prefilled with your draft.",
        );
        return;
      }
      // "sendFromHr" is the LEGACY id: stored conversations replay cards persisted
      // before 2026-08-10, when this button sent via Graph in one click. It now maps
      // onto the draft flow — deliberately, so no replayed card can ever send mail.
      case "sendFromHr":
      case "draftFromHr": {
        // ALWAYS the Graph path, in every mailbox and every client. Office.js cannot
        // set a compose form's From address, and a native reply inside an automapped
        // shared mailbox composes as the USER, not the mailbox (verified 2026-08-11
        // — the earlier shortcut here was wrong). Creating the draft in the shared
        // mailbox is the only thing that guarantees the HR address.
        //
        // Create a reply draft INSIDE the HR shared mailbox. Creating it there is
        // what sets the From address to the HR team — nothing to fill in, nothing to
        // get wrong. Nothing is sent: the draft attaches to the conversation in the
        // shared mailbox, so it appears at the top of that thread there.
        //
        // AND THEN OPEN IT. Restored 2026-08-26, having been removed on 2026-08-11 for
        // bouncing desktop users into a browser. Two things changed that decision:
        //
        //   1. Noah, testing in the real client: "I don't want users to have to go
        //      into drafts to send an email." Landing a draft in a folder and telling
        //      someone where to find it is not a reply flow, it is a filing system.
        //   2. He also confirmed the behaviour that makes this work: opening an item
        //      that LIVES in the shared mailbox and replying gives From = the shared
        //      mailbox. This draft is exactly such an item, so the editor it opens in
        //      shows the HR address — which Office.js cannot produce by any other
        //      route, since `displayReplyForm` has no `from` and MessageCompose
        //      exposes `from` as a getter only.
        //
        // The browser-window cost is real and unchanged; what changed is that we now
        // know it buys the one thing the alternative cannot. Failing to open is NOT
        // an error — the draft is already safely in the mailbox, so the toast falls
        // back to telling the user where it is rather than implying it was lost.
        void (async () => {
          const text = draftTextFor(card);
          const messageId = getRestItemId();
          // internetMessageId is what actually resolves in the shared mailbox;
          // messageId is only a fallback for when the user has that mailbox open.
          const internetMessageId = getInternetMessageId();
          if (!text || (!messageId && !internetMessageId)) {
            setToast("Open the email you're replying to, with a draft ready, to draft from the HR mailbox.");
            return;
          }
          const res = await createHrReplyDraft({ messageId, internetMessageId, text });
          if (res.ok) {
            // SAME MECHANISM AS THE PERSONAL BUTTON: hand the item to Outlook and let
            // Outlook open its own editor. The personal path uses displayReplyForm on
            // the message being replied to; this uses displayMessageForm on the draft
            // Graph just created. Neither sets a From, because neither can — the
            // difference in sender comes entirely from WHICH ITEM is opened, and this
            // one lives in the shared mailbox.
            //
            // NOT a browser window. `res.webLink` would open one and is deliberately
            // ignored: it takes the user out of Outlook, which is a worse experience
            // than the thing it was meant to fix.
            const trace = res.draftId
              ? await displayExistingMessage(res.draftId, res.ewsId)
              : { outcome: "failed" as const, attempts: [], api: "none" as const };
            // REPORTED ALWAYS, success included. Which of the three id candidates
            // Outlook accepts is the open question this feature turns on, and a
            // success that does not say WHICH one worked settles nothing. Silent and
            // fire-and-forget; it cannot affect the draft or the user.
            void reportComposeProbe(trace);
            if (res.signatureImagesFailed > 0) {
              setToast("Draft created, but an image in your signature didn't attach. Check the draft before sending.");
            } else if (trace.outcome === "ok") {
              setToast("Opening the reply — it will send as the HR team.");
            } else {
              // The draft EXISTS and is correct; only the shortcut to it failed. Say
              // exactly that, rather than implying the work was lost — and do not
              // silently substitute a browser window, because then nobody would ever
              // learn that the native open does not work here.
              setToast("Draft saved in the HR mailbox — open that conversation there to review and send.");
              // Put the diagnosis ON SCREEN too, not only in the server log. This is
              // being rolled out to test whether Outlook accepts any of the three id
              // forms, and the person doing that testing is in Outlook, not in a log
              // viewer — the same reason the SSO failures print their code in the pane
              // rather than the console. Codes only; no ids, no content.
              const why = trace.attempts.map((a) => `${a.kind}:${a.code ?? a.message ?? "no"}`).join(", ");
              if (why) {
                pushItem({
                  id: nextId(),
                  kind: "bubble",
                  role: "system",
                  html: `Couldn't open the draft in Outlook (${escapeHtml(trace.api)} API). Tried — ${escapeHtml(why)}.`,
                });
              }
            }
            return;
          }
          switch (res.reason) {
            case "unauthorized":
              setToast(SSO_TOAST);
              return;
            case "mailbox_disabled":
              setToast("Shared-mailbox access isn't enabled yet (waiting on access grants).");
              return;
            case "mailbox_busy":
              // Microsoft is moving the mailbox. Transient, self-healing, and the
              // only useful instruction is to wait — not to contact anyone.
              setToast(res.detail ?? "The HR mailbox is temporarily unavailable. Try again in a few minutes.");
              return;
            case "not_in_shared_mailbox":
              setToast(res.detail ?? "That message isn't in the HR shared mailbox.");
              return;
            case "bad_request":
              setToast("Couldn't create the draft — the draft text or the message reference was missing.");
              return;
            default:
              setToast("Couldn't create the draft — check with the team.");
          }
        })();
        return;
      }
      case "openNewMessage": {
        // Draft-from-scratch: open Outlook's native new-message compose prefilled.
        const subject = (action.data?.subject as string) ?? "";
        composeToast(
          displayNewMessageForm(subject, draftTextFor(card)),
          "Draft opened as a new email.",
          "In Outlook, this opens a new email prefilled with your draft.",
        );
        return;
      }
      case "draftReply":
        if (emailId) setCurrentEmailId(emailId);
        runTurn("Draft a response for this email", emailId);
        return;
      case "openEmail":
        if (emailId) setCurrentEmailId(emailId);
        runTurn("Summarize this email", emailId);
        return;
      default:
        if (emailId) setCurrentEmailId(emailId);
        runTurn(action.label, emailId);
    }
  }

  function onResult(result: CardResult) {
    setCurrentEmailId(result.id);
    if (result.rowAction === "summarizeEmail" || !result.rowAction) {
      runTurn("Summarize this email", result.id);
    } else {
      runTurn(result.rowAction, result.id);
    }
  }

  // REMOVED 2026-08-11: a render-time swap that, inside the shared mailbox, replaced
  // both draft buttons with a single native-reply button.
  //
  // CORRECTED 2026-08-26 — the original note here overstated its own finding.
  //
  // It said: "replying natively to a message in an automapped shared mailbox composes
  // from the USER'S OWN address". Noah retested in the real client and got the
  // OPPOSITE: click a message in the shared mailbox, press Reply, and From is
  // populated as the shared mailbox. So native reply is not the broken half.
  //
  // What IS confirmed, by the same round of testing, is the half that actually
  // mattered: the add-in's own button ("Draft reply from me", i.e.
  // `displayReplyForm`) composes as the USER even for a message in the shared
  // mailbox. Office.js does not inherit the surface's mailbox context the way
  // Outlook's own Reply button does, and it exposes no `from` to set — so the
  // render-time swap was still wrong to remove the HR button, just not for the
  // reason recorded.
  //
  // The lesson worth keeping is narrower than what was written: DO NOT infer the From
  // address from where the user is. Infer nothing. The only address we can guarantee
  // is the one on an item that LIVES in the shared mailbox, which is why the HR path
  // creates it there via Graph.
  //
  // Office.js cannot set From on any compose form it opens — there is no parameter
  // and the field is read-only in compose. So creating the draft in the shared
  // mailbox via Graph is not merely one option, it is the ONLY way to guarantee the
  // HR From address, in every client and every view. Both buttons are therefore
  // unconditional now. The shared/own signal is still tracked in office.ts, but only
  // for the mailbox_context analytics column — no UI depends on it.

  // The pane must not render until we know this is Outlook — NOT merely until we
  // know it isn't.
  //
  // The first version of this gate had it backwards: it rendered the chat while
  // undecided and swapped in the notice afterwards. In a browser that meant ~20s of
  // a fully interactive pane, and because the browser fallbacks in office.ts invent
  // a "current email" (`getCurrentEmailMeta` returns Dana Williams' PTO request), the
  // page appeared to be showing somebody's actual mail to anyone who typed the
  // hostname in. It never was — that string is a local fixture and every /api call
  // behind it 401s — but "it only LOOKS like a mailbox leak" is not a sentence worth
  // having to say to a client's security reviewer.
  //
  // So: decide first, render second. `decided` is true as soon as ANY of the three
  // answers arrives, and in the two common cases that is immediate —
  //   - inside Outlook, hostUp is already true at mount (or within one 250ms poll)
  //   - in local dev, /healthz returns officeOnly:false in ~150ms
  // Only the deployed-browser case waits, and it waits on a neutral splash.
  const decided = hostUp || officeOnly === false || (officeOnly === true && hostSettled);
  if (!decided) {
    // Branded rather than blank. This window is ~5s in a browser, because
    // index.html loads Office.js synchronously from Microsoft's CDN before React
    // runs at all — so the wait is unavoidable without making that script async,
    // which risks Office's own initialisation timing for a cosmetic gain.
    // Inside Outlook hostUp is true at mount, so this state never paints.
    // An empty surface for five seconds reads as a broken page; this reads as loading.
    return (
      <div className="tp-web tp-deciding" aria-busy="true" aria-label="Starting">
        <div className="tp-deciding-mark" aria-hidden="true">
          B
        </div>
      </div>
    );
  }
  if (officeOnly && !hostUp) return <OutsideOutlook />;

  return (
    <div className="tp-web">
      {/* Outlook draws the pane header (title/close), so no in-app brand row:
          just the context strip with the mode badge. */}
      <div className="tp-context">
        <span className="ctx-dot" />
        {/* Not "Reading: ..." unconditionally — the pane works with no email at
            all (policy questions, mailbox search, drafting from scratch), and the
            old wording implied a message was required. */}
        {selected.length >= 2 ? (
          <span className="ctx-text">
            <b>{selected.length} messages</b> selected
          </span>
        ) : meta.subject ? (
          <span className="ctx-text" title={meta.fromName ? `${meta.subject} — ${meta.fromName}` : meta.subject}>
            <b>{meta.subject}</b>
            {meta.fromName ? ` — ${meta.fromName}` : ""}
          </span>
        ) : (
          <span className="ctx-text ctx-muted">HR Assistant — ask about policies or the HR mailbox</span>
        )}
        {mode && (
          <span className={`mode-badge ${mode}`} title={mode === "live" ? "Connected to a live model" : "No model key — using sample data"}>
            {mode === "live" ? "Live" : "Sample"}
          </span>
        )}
        {/* ALWAYS PRESENT (2026-08-27). Starting a new chat is a top-level action and
            should not be reached by opening a menu of OLD chats to find it — Noah:
            "you shouldn't have to click previous chat to create a new chat."

            It used to render only when historyAvailable === false, on the reasoning
            that the dropdown carries its own entry when there is a store. That kept
            exactly one control on screen, but it buried the commonest action behind
            the rarest one. The dropdown's duplicate entry has since been removed, so
            this is now the ONLY way to start a chat — one action, one place.

            Sits BEFORE the dropdown: starting a chat precedes reopening an old one,
            and the leftmost control should be the one used most.

            Unconditional rather than gated on historyAvailable, so it also cannot
            flicker into existence while /healthz is still in flight. */}
        <button className="ctx-reset" title="Start a new chat" onClick={resetChat}>
          New chat
        </button>
        {signature && (
          <button className="ctx-reset ctx-signature" title="Your signature on HR drafts" onClick={() => setSignatureSheet("settings")}>
            Signature
          </button>
        )}
        <ConversationMenu
          available={historyAvailable}
          conversations={conversations}
          activeId={conversationId}
          loading={historyLoading}
          onOpen={() => void refreshConversations()}
          onSelect={(id) => void openConversation(id)}
          onRemove={(id) => void removeConversation(id)}
        />
      </div>

      <div id="chat" className="tp-chat" ref={chatRef}>
        {items.map((it) =>
          it.kind === "bubble" ? (
            <Bubble key={it.id} role={it.role} html={it.html} />
          ) : it.kind === "feedback" ? (
            <Feedback
              key={it.id}
              usageEventId={it.usageEventId}
              value={it.rating ?? null}
              onChange={(rating) =>
                setItems((prev) =>
                  prev.map((p) => (p.id === it.id && p.kind === "feedback" ? { ...p, rating } : p)),
                )
              }
            />
          ) : (
            <CardView key={it.id} card={it.card} onAction={onAction} onResult={onResult} onOpen={openUrl} />
          ),
        )}
        {thinking && <Typing />}
      </div>

      <Chips chips={chips} onChip={(t) => runTurn(t)} />
      <Composer ref={inputRef} onSend={(t) => runTurn(t)} disabled={thinking} />

      {signature && signatureSheet && (
        <SignatureSetup
          mode={signatureSheet}
          saved={signature.status === "confirmed" ? signature.html : null}
          canDetect={signature.canDetect}
          onClose={closeSignatureSheet}
        />
      )}

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
